import { env } from "@/env";
import type { AnalyticsEvent, AnalyticsProviderInterface } from "../types";

export interface GTMConfig {
  id: string;
  enabled: boolean;
  debug?: boolean;
  auth?: string; // GTM environment auth token
  preview?: string; // GTM environment preview name
}

// Get GTM ID from environment variables
export const GTM_ID = (env.PUBLIC_GTM_ID ?? "").trim();
const GTM_SUPER_PROPS_KEY = "gtm_super_props";

function hasGtmContainerId(id: string): boolean {
  return id.length > 0;
}

export interface EnvGTMConfig {
  development: GTMConfig;
  production: GTMConfig;
  [key: string]: GTMConfig;
}

export const defaultGTMConfig: EnvGTMConfig = {
  development: {
    id: GTM_ID,
    enabled: false,
    debug: true,
    auth: env.PUBLIC_GTM_AUTH,
    preview: env.PUBLIC_GTM_PREVIEW,
  },
  production: {
    id: GTM_ID,
    enabled: true,
    debug: false,
    // No auth/preview for production - uses default GTM container
  },
};

/**
 * Gets the GTM configuration for the current environment
 *
 * GTM supports environment-specific configurations through auth and preview parameters:
 * - The same GTM container ID is used across all environments
 * - Different GTM environments (preview, development, etc.) are accessed using auth and preview params
 * - Production uses the default published container with no special parameters
 *
 * @param config Optional custom configuration to use instead of the default
 * @returns The GTM configuration for the current environment
 */
export const getGTMConfig = (
  config: EnvGTMConfig = defaultGTMConfig,
): GTMConfig => {
  const environment = env.PUBLIC_ENV || "development";
  // Map PUBLIC_ENV values to our config keys
  let configKey: string;
  if (
    //environment === "staging" ||
    environment === "beta" ||
    environment === "main"
  ) {
    configKey = "production";
  } else {
    configKey = "development"; // fallback
  }
  const raw = config[configKey] || config.development;
  const id = (raw.id ?? "").trim();
  const enabled = hasGtmContainerId(id) && raw.enabled;
  return { ...raw, id, enabled };
};

/** GTM container tags (e.g. legacy form trackers) assume marketing-site DOM; skip on local dev. */
function isGtmHostAllowed(): boolean {
  if (process.env.NODE_ENV === "development") {
    return false;
  }
  if (typeof window === "undefined") {
    return true;
  }
  const host = window.location.hostname;
  return host !== "localhost" && host !== "127.0.0.1";
}

export class GTMProvider implements AnalyticsProviderInterface {
  readonly providerType = "GoogleTagManager";
  private initialized = false;
  private config: GTMConfig;

  private scriptEl?: HTMLScriptElement;
  private noscriptEl?: HTMLElement;

  constructor() {
    this.config = getGTMConfig();
    this.initGTM(this.config);
  }

  private initGTM(config: GTMConfig): void {
    if (
      typeof window === "undefined" ||
      !config.enabled ||
      !hasGtmContainerId(config.id) ||
      !isGtmHostAllowed()
    ) {
      return; // Skip: SSR, disabled, missing container ID, or disallowed host
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Push environment info
    window.dataLayer.push({ environment: env.PUBLIC_ENV });
    if (config.debug) {
      window.dataLayer.push({ "gtm.debug": true });
    }

    // Build GTM params
    // let gtmParams = config.id;
    // if (config.auth && config.preview) {
    //   gtmParams += `&gtm_auth=${config.auth}&gtm_preview=${config.preview}`;
    // }

    // Inject script
    this.scriptEl = document.createElement("script");

    this.scriptEl.setAttribute("data-gtm-id", config.id);
    this.scriptEl.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://server.birdwatch.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${config.id}');`;

    if (!document.querySelector(`script[data-gtm-id="${config.id}"]`)) {
      document.head.appendChild(this.scriptEl);
    }

    // Noscript fallback
    this.noscriptEl = document.createElement("noscript");
    this.noscriptEl.setAttribute("data-gtm-id", config.id);
    const iframe = document.createElement("iframe");
    iframe.src = `https://server.birdwatch.com/ns.html?id=${config.id}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    this.noscriptEl.appendChild(iframe);
    if (!document.querySelector(`noscript[data-gtm-id="${config.id}"]`)) {
      document.body.appendChild(this.noscriptEl);
    }

    this.initialized = true;
  }

  sendEvent(eventName: string, properties?: AnalyticsEvent): void {
    if (typeof window === "undefined" || !this.initialized) return;
    const superProps = this.getSuperProps();
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      event_data: { ...properties, ...superProps },
    });
  }

  identify(userId: string, traits?: Record<string, any>): void {
    if (typeof window === "undefined" || !this.initialized) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "userIdentified",
      userId,
      userTraits: traits || {},
    });
  }

  setSuperProps(props: Record<string, any>): void {
    localStorage.setItem(GTM_SUPER_PROPS_KEY, JSON.stringify(props));
  }

  private getSuperProps(): Record<string, any> {
    const superProps = localStorage.getItem(GTM_SUPER_PROPS_KEY);
    return superProps ? JSON.parse(superProps) : {};
  }

  reset(): void {
    localStorage.removeItem(GTM_SUPER_PROPS_KEY);
  }
}

// Add TypeScript interface for window object to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}
